export default function HeroSection() {
  return (
    <section className="relative min-h-screen flex items-center pt-24 overflow-hidden" style={{ background: 'linear-gradient(135deg, #09090f 0%, #0e0e1a 50%, #09090f 100%)' }}>
      {/* Ambient glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[600px] rounded-full opacity-10 pointer-events-none" style={{ background: 'radial-gradient(circle, #c9a84c 0%, transparent 70%)' }} />
      <div className="absolute bottom-0 right-0 w-[600px] h-[600px] rounded-full opacity-5 pointer-events-none" style={{ background: 'radial-gradient(circle, #e8c87a 0%, transparent 70%)' }} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full py-16 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left */}
          <div className="text-left">
            <div className="mb-8">
              <span
                className="inline-flex items-center gap-2 px-5 py-2.5 text-xs font-semibold tracking-[0.2em] uppercase border"
                style={{
                  borderColor: 'rgba(201,168,76,0.4)',
                  color: '#c9a84c',
                  background: 'rgba(201,168,76,0.06)',
                  borderRadius: '2px',
                }}
              >
                <i className="ri-flashlight-line" />
                Shaping The Future With AI
              </span>
            </div>

            <div className="mb-8">
              <h2 className="text-5xl md:text-6xl lg:text-7xl font-light mb-6 leading-none tracking-tight">
                <span className="block text-white/90 font-extralight mb-2">Brilliance.</span>
                <span
                  className="block font-bold"
                  style={{ background: 'linear-gradient(135deg, #c9a84c 0%, #f0d98a 50%, #c9a84c 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}
                >
                  Engineered.
                </span>
              </h2>
              <div className="mb-8 flex items-center gap-3">
                {['Illuminate', 'Innovate', 'Elevate'].map((w, i) => (
                  <span key={w}>
                    <span className="text-sm md:text-base font-medium text-white/40 tracking-[0.15em] uppercase">{w}</span>
                    {i < 2 && <span className="ml-3 text-[#c9a84c]/40">·</span>}
                  </span>
                ))}
              </div>
            </div>

            <p className="text-base md:text-lg mb-4 leading-relaxed text-white/60 font-light max-w-xl">
              Éclat-Gen is a strategic AI alliance empowering leaders across Life Sciences, Pharma, Semiconductor &amp; Materials Science, and Deep Tech — driving intelligent digital transformation from legacy infrastructure to sovereign, future-ready innovation.
            </p>
            <p className="text-sm md:text-base mb-12 leading-relaxed font-medium italic max-w-xl" style={{ color: '#c9a84c' }}>
              Because true brilliance isn&apos;t just built — it&apos;s engineered with precision, purpose, and the power of AI.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-16">
              <a
                href="#services"
                className="px-8 py-4 text-sm md:text-base font-semibold whitespace-nowrap cursor-pointer transition-all duration-300 text-center tracking-wide"
                style={{
                  background: 'linear-gradient(135deg, #c9a84c, #e8c87a)',
                  color: '#09090f',
                  borderRadius: '4px',
                }}
              >
                Strategic Consultancy
              </a>
              <a
                href="#solutions"
                className="px-8 py-4 text-sm md:text-base font-semibold whitespace-nowrap cursor-pointer transition-all duration-300 text-center tracking-wide border text-white/80 hover:text-[#c9a84c]"
                style={{
                  borderColor: 'rgba(201,168,76,0.4)',
                  background: 'rgba(201,168,76,0.05)',
                  borderRadius: '4px',
                }}
              >
                AI Advisory Services
              </a>
            </div>

            <div className="grid gap-4">
              {[
                { icon: 'ri-flashlight-line', title: 'Illuminate', desc: 'Uncovering transformative opportunities hidden within your data and processes' },
                { icon: 'ri-settings-4-line', title: 'Innovate', desc: 'Engineering intelligent systems that adapt, scale, and evolve with your ambitions' },
                { icon: 'ri-rocket-line', title: 'Elevate', desc: 'Empowering organizations to transcend limits and achieve luminous breakthrough results' },
              ].map((item) => (
                <div
                  key={item.title}
                  className="p-5 transition-all duration-300 cursor-pointer group"
                  style={{
                    background: 'rgba(201,168,76,0.04)',
                    border: '1px solid rgba(201,168,76,0.15)',
                    borderRadius: '8px',
                  }}
                >
                  <div className="flex items-start space-x-4">
                    <div
                      className="w-10 h-10 flex items-center justify-center flex-shrink-0 transition-all duration-300 group-hover:scale-110"
                      style={{ background: 'rgba(201,168,76,0.15)', borderRadius: '6px' }}
                    >
                      <i className={`${item.icon} text-lg`} style={{ color: '#c9a84c' }} />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-white/90 mb-1 tracking-wide">{item.title}</h3>
                      <p className="text-xs text-white/45 font-light leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right */}
          <div className="relative">
            <div className="mb-6">
              <div className="relative overflow-hidden" style={{ borderRadius: '12px', border: '1px solid rgba(201,168,76,0.2)' }}>
                <video
                  className="w-full h-64 object-cover"
                  controls
                  poster="https://public.readdy.ai/ai/img_res/ebd64316bcdec8b610e0093fbd60453d.jpg"
                >
                  Your browser does not support the video tag.
                </video>
                <div className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(135deg, rgba(201,168,76,0.08) 0%, transparent 60%)' }} />
                <div className="absolute top-3 left-3 px-3 py-1.5 text-xs font-semibold tracking-wide" style={{ background: 'rgba(201,168,76,0.9)', color: '#09090f', borderRadius: '3px' }}>
                  LIVE DEMO
                </div>
              </div>
            </div>
            <div className="relative" style={{ borderRadius: '12px', overflow: 'hidden', border: '1px solid rgba(201,168,76,0.15)' }}>
              <img
                alt="Éclat-Gen AI Innovation Lab"
                className="w-full h-[420px] object-cover object-top"
                src="https://readdy.ai/api/search-image?query=Dramatic%20dark%20ultra-modern%20AI%20research%20laboratory%20with%20ambient%20golden%20lighting%2C%20scientists%20and%20engineers%20working%20at%20glowing%20workstations%20with%20large%20holographic%20displays%20showing%20machine%20learning%20algorithms%20and%20molecular%20data%2C%20luxurious%20dark%20environment%20with%20obsidian%20surfaces%20and%20gold%20accent%20lighting%2C%20premium%20technology%20research%20facility%20with%20cutting-edge%20computing%20infrastructure%2C%20team%20of%20diverse%20professionals%20in%20sophisticated%20dark%20attire%20collaborating%20around%20advanced%20AI%20systems%2C%20representing%20elite%20next-generation%20artificial%20intelligence%20innovation%20with%20dark%20dramatic%20aesthetic%20and%20golden%20illumination&width=600&height=840&seq=eclat-gen-lab-hero&orientation=portrait"
              />
              <div className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(to top, rgba(9,9,15,0.8) 0%, transparent 50%)' }} />
              <div className="absolute bottom-5 left-5 right-5">
                <div className="flex items-center gap-3 p-3" style={{ background: 'rgba(9,9,15,0.85)', backdropFilter: 'blur(16px)', borderRadius: '8px', border: '1px solid rgba(201,168,76,0.2)' }}>
                  <div className="w-2 h-2 rounded-full animate-pulse" style={{ background: '#c9a84c' }} />
                  <span className="text-xs text-white/70 font-medium tracking-wide">AI Systems Active — 47 Models Running</span>
                </div>
              </div>
            </div>

            {/* Floating stats */}
            <div className="absolute -left-6 top-1/3 p-4 hidden lg:block" style={{ background: 'rgba(9,9,15,0.92)', backdropFilter: 'blur(20px)', borderRadius: '10px', border: '1px solid rgba(201,168,76,0.25)' }}>
              <div className="text-2xl font-bold mb-0.5" style={{ background: 'linear-gradient(135deg, #c9a84c, #e8c87a)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>150+</div>
              <div className="text-xs text-white/50 tracking-wide">Projects Delivered</div>
            </div>
            <div className="absolute -right-4 bottom-1/3 p-4 hidden lg:block" style={{ background: 'rgba(9,9,15,0.92)', backdropFilter: 'blur(20px)', borderRadius: '10px', border: '1px solid rgba(201,168,76,0.25)' }}>
              <div className="text-2xl font-bold mb-0.5" style={{ background: 'linear-gradient(135deg, #c9a84c, #e8c87a)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>98%</div>
              <div className="text-xs text-white/50 tracking-wide">Success Rate</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
